import _ from 'lodash';
import Sticker from './Sticker';

const isTouch = !!('ontouchstart' in window);
const PAINT_START = isTouch ? 'touchstart' : 'mousedown';
const PAINT_MOVE = isTouch ? 'touchmove' : 'mousemove';
const PAINT_END = isTouch ? 'touchend' : 'mouseup';
let IS_MOVING = false;
let MOUSE_DOWN = false;

export default class Canvas {
  canvas = null;
  ctx = null;
  ppts = [];
  completePaths = [];
  brushColor = '';
  brushSize = 10;
  brushShape = 'round';
  currentSticker = null;
  point = {
    x: 0,
    y: 0
  };

  constructor (id = '', config = {}) {
    this.id = id;
    this.config = config;
  }

  /**
   * Initializes the canvas
   * @return {null}
   */
  init () {
    this.canvas = document.getElementById(this.id);
    this.ctx = this.canvas.getContext('2d');
    this.ctx.fillStyle = this.config.canvasColor || '#ffffff';
    this.setBrushShape(this.config.brushShape || 'round');
    this.clearCanvas();
    this.setBrushSize(this.config.brushSize || 10);
    this.setBrushColor(this.config.brushColor || '#000000');
    this.addListeners();
  }

  /**
   * Returns Offset
   * @param elem
   * @returns {{top: *, left: *}}
   */
  getOffset (elem) {
    let offsetTop = 0;
    let offsetLeft = 0;

    do {
      if (!isNaN(elem.offsetLeft)) {
        offsetTop += elem.offsetTop;
        offsetLeft += elem.offsetLeft;
      }
      elem = elem.offsetParent;
    } while (elem);

    return {
      left: offsetLeft,
      top: offsetTop
    };
  }

  /**
   * Assigns point from a given event
   * @param point
   * @param e
   */
  setPointFromEvent (point, e) {
    if (isTouch) {
      point.x = e.changedTouches[0].pageX - this.getOffset(e.target).left;
      point.y = e.changedTouches[0].pageY - this.getOffset(e.target).top;
    } else {
      point.x = e.offsetX !== undefined ? e.offsetX : e.layerX;
      point.y = e.offsetY !== undefined ? e.offsetY : e.layerY;
    }
  }

  /**
   * Paints the path where the cursor pass
   * @param e
   */
  onMove (e) {
    if (e) {
      e.preventDefault();
      this.setPointFromEvent(this.point, e);
    }
    if (!IS_MOVING) {
      if (this.currentSticker) {
        this.currentSticker.onMove(this.point.x, this.point.y);
        this.repaint();
      }
      return;
    }
    // Saving all the points in an array
    this.ppts.push({
      x: this.point.x,
      y: this.point.y
    });

    this.paintPath(this.ppts);
  }

  /**
   * when a drop event is handled
   * @param {*} e
   */
  onDrop (ev) {
    this.setPointFromEvent(this.point, ev);
    ev.preventDefault();
    const sticker = ev.dataTransfer.getData('sticker');
    if (sticker) {
      this.setSticker(sticker);
      this.currentSticker.x = this.point.x - this.currentSticker.width / 2;
      this.currentSticker.y = this.point.y - this.currentSticker.height / 2;
    }
  }

  /**
   * paint a single path
   * @param {*} path point array
   * @param {*} color stroke color
   * @param {*} shape stroke shape
   * @param {*} size stroke size
   */
  paintPath (path, color, shape, size) {
    if (path.length < 3) return;
    this.ctx.strokeStyle = color || this.brushColor;
    this.ctx.lineJoin = this.ctx.lineCap = shape || this.brushShape;
    this.ctx.lineWidth = size || this.brushSize;
    if (path.length === 3) {
      const b = path[0];
      this.ctx.fillStyle = color || this.brushColor;
      this.ctx.beginPath();
      this.ctx.arc(b.x, b.y, this.ctx.lineWidth / 2, 0, Math.PI * 2, !0);
      this.ctx.fill();
      this.ctx.closePath();
      this.ctx.fillStyle = this.config.canvasColor || '#ffffff';
      return;
    }
    this.ctx.lineJoin = this.ctx.lineCap = shape || this.brushShape;
    this.ctx.lineWidth = size || this.brushSize;
    this.ctx.beginPath();
    this.ctx.moveTo(path[0].x, path[0].y);

    for (let i = 1; i < path.length - 2; i++) {
      const c = (path[i].x + path[i + 1].x) / 2;
      const d = (path[i].y + path[i + 1].y) / 2;
      this.ctx.quadraticCurveTo(path[i].x, path[i].y, c, d);
    }

    // For the last 2 points
    this.ctx.quadraticCurveTo(
      path[path.length - 2].x,
      path[path.length - 2].y,
      path[path.length - 1].x,
      path[path.length - 1].y
    );
    this.ctx.stroke();
  }

  /**
   * Copies temporary Image
   */
  endPath () {
    if (this.currentSticker) {
      this.currentSticker.onRelease();
    }
    if (IS_MOVING) {
      IS_MOVING = false;
      this.completePaths.push({
        type: 'path',
        path: this.ppts,
        color: this.brushColor,
        shape: this.brushShape,
        size: this.brushSize
      });
      this.ppts = [];
    }
  }

  /**
   * Starts to paint
   * @param e
   */
  startPath (e) {
    e.preventDefault();
    if (!this.currentSticker) {
      IS_MOVING = true;
      this.setPointFromEvent(this.point, e);
      this.ppts.push({
        x: this.point.x,
        y: this.point.y
      });
      this.ppts.push({
        x: this.point.x,
        y: this.point.y
      });
      this.onMove();
    } else {
      this.currentSticker.onClick(this.point.x, this.point.y);
      if (this.currentSticker.isReady) {
        if (this.currentSticker.img) {
          this.completePaths.push({
            type: 'sticker',
            sticker: this.currentSticker
          });
        }
        this.currentSticker = null;
        this.repaint();
      }
    }
  }

  /**
   * On Mouse press
   */
  onMouseDown () {
    MOUSE_DOWN = true;
  }

  /**
   * On Mouse release
   */
  onMouseUp () {
    MOUSE_DOWN = false;
  }

  /**
   * On Mouse Enters Canvas
   * @param e
   */
  onMouseEnters (e) {
    // If the mouse is down when it enters the canvas, start a path
    if (MOUSE_DOWN) {
      this.startPath(e);
    }
  }

  /**
   * On Mouse Leaves Canvas
   * @param e
   */
  onMouseLeaves (e) {
    // If the mouse is down when it leaves the canvas, end the path
    if (MOUSE_DOWN) {
      this.endPath(e);
    }
  }

  /**
   * Remove Listeners
   */
  removeListeners () {
    this.canvas.removeEventListener(
      PAINT_START,
      this.startPath.bind(this),
      false
    );
    this.canvas.removeEventListener(PAINT_MOVE, this.onMove.bind(this), false);
    this.canvas.removeEventListener(
      PAINT_END,
      this.endPath.bind(this),
      false
    );
    this.canvas.removeEventListener('drop', this.onDrop.bind(this), false);

    if (!isTouch) {
      document.body.removeEventListener(
        'mousedown',
        this.onMouseDown.bind(this)
      );
      document.body.removeEventListener('mouseup', this.onMouseUp.bind(this));
      this.canvas.removeEventListener(
        'mouseenter',
        this.onMouseEnters.bind(this)
      );
      this.canvas.removeEventListener(
        'mouseleave',
        this.onMouseLeaves.bind(this)
      );
    }
  }

  /**
   * set sticker
   * @param stickerPath path of sticker
   * @param width desired width for sticker
   * @param height desired height for sticker
   */
  setSticker (stickerPath, width = null, height = null) {
    this.currentSticker = new Sticker(stickerPath, this.ctx);
    this.repaint();
  }

  /**
   * set brush color
   * @param color new color for brush
   */
  setBrushColor (color) {
    this.brushColor = color;
    this.ctx.strokeStyle = color;
  }

  /**
   * set brush size
   * @param size new size for brush
   */
  setBrushSize (size) {
    this.brushSize = size;
    this.ctx.lineWidth = size;
  }

  /**
   * set brush Shape
   * @param shape new shape for brush, can be "round" or "square"
   */
  setBrushShape (shape) {
    this.brushShape = shape;
    if (shape !== 'round' && shape !== 'square') {
      shape = 'round';
    }
    this.ctx.lineJoin = this.ctx.lineCap = shape;
  }

  /**
   * clears
   */
  clearCanvas () {
    this.currentSticker = null;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    this.completePaths = [];
  }

  /**
   * undo
   */
  undo () {
    if (this.currentSticker !== null) {
      this.currentSticker = null;
      this.repaint();
      return;
    }
    if (this.completePaths.length === 0) {
      return;
    }
    this.completePaths.pop();
    this.repaint();
  }

  /**
   * repaints all stickers and paths
   */
  repaint (topLayers) {
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    _.each(this.completePaths, path => {
      if (path.type === 'path') {
        this.paintPath(path.path, path.color, path.shape, path.size);
      } else {
        path.sticker.draw();
      }
    });
    if (this.currentSticker) {
      this.currentSticker.draw();
    }
    if (topLayers) {
      for (let i = 0; i < topLayers.length; i++) {
        if (topLayers[i]) {
          this.ctx.drawImage(topLayers[i], 0, 0);
        }
      }
    }
  }

  /**
   * Adds Listeners
   */
  addListeners () {
    this.canvas.addEventListener(PAINT_START, this.startPath.bind(this), false);
    this.canvas.addEventListener(PAINT_MOVE, this.onMove.bind(this), false);
    this.canvas.addEventListener(PAINT_END, this.endPath.bind(this), false);
    this.canvas.addEventListener('drop', this.onDrop.bind(this), false);

    if (!isTouch) {
      this.canvas.addEventListener('mousedown', this.onMouseDown.bind(this));
      document.body.addEventListener('mouseup', this.onMouseUp.bind(this));
      this.canvas.addEventListener('mouseenter', this.onMouseEnters.bind(this));
      this.canvas.addEventListener('mouseleave', this.onMouseLeaves.bind(this));
    }
  }
}
